// Calendar utilities: leap years, days-in-month, weekdays, holidays for South Tyrol / Italy

/**
 * Parse a YYYY-MM-DD string into a Date anchored at LOCAL midnight.
 * This avoids the off-by-one bug caused by `new Date("2026-04-30")` being
 * interpreted as UTC midnight, which can shift to the previous day in
 * negative-offset timezones.
 */
export function parseISODateLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const parts = iso.slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * Inclusive day count between two ISO date strings.
 * Returns null on invalid input or end < start.
 */
export function inclusiveDays(startIso, endIso) {
  const s = parseISODateLocal(startIso);
  const e = parseISODateLocal(endIso);
  if (!s || !e || e < s) return null;
  return Math.round((e - s) / 86400000) + 1;
}

export function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year, month /* 0-11 */) {
  if (month === 1) return isLeapYear(year) ? 29 : 28;
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
}

// Returns Date for Easter Sunday in given year (Gregorian Computus / Anonymous Gregorian)
export function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const L = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * L) / 451);
  const month = Math.floor((h + L - 7 * m + 114) / 31); // 3=March, 4=April
  const day = ((h + L - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmt(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function ymd(year, month /* 0-11 */, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// Returns Map<YYYY-MM-DD, holidayName> for the given year, including South-Tyrol & Italy holidays
export function holidaysForYear(year) {
  const easter = easterSunday(year);
  const easterMon = addDays(easter, 1);
  const pentecost = addDays(easter, 49);
  const pentecostMon = addDays(easter, 50);

  const list = [
    [`${year}-01-01`, "Neujahr"],
    [`${year}-01-06`, "Heilige Drei Könige"],
    [`${year}-03-19`, "Josefitag"],         // South Tyrol
    [fmt(easterMon),  "Ostermontag"],
    [`${year}-04-25`, "Tag der Befreiung"],
    [`${year}-05-01`, "Tag der Arbeit"],
    [fmt(pentecostMon), "Pfingstmontag"],   // South Tyrol
    [`${year}-06-02`, "Tag der Republik"],
    [`${year}-08-15`, "Mariä Himmelfahrt"],
    [`${year}-11-01`, "Allerheiligen"],
    [`${year}-12-08`, "Mariä Empfängnis"],
    [`${year}-12-25`, "Weihnachten"],
    [`${year}-12-26`, "Stephanstag"],
  ];
  // San Francesco d'Assisi: nationaler Feiertag ab 2026 (Legge n. 151/2025).
  if (year >= 2026) list.push([`${year}-10-04`, "San Francesco d'Assisi"]);
  return new Map(list);
}

// JS getDay: 0=Sun..6=Sat. Returns true for Sat/Sun.
export function isWeekend(date) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

// Returns weekday short label (Mo..So) from a Date
export function weekdayShortDe(date) {
  // JS Sun=0..Sat=6 → reorder to Mon..Sun
  const labels = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return labels[date.getDay()];
}

// Returns true if the given YYYY-MM-DD lies within [start, end] inclusive (string compare since ISO)
export function dateInRange(date, start, end) {
  return date >= start && date <= end;
}
